import useMediaQuery, { mediaQueryPoints } from '@/hooks/useMediaQuery';
import { formatNumber } from '@/utils/formatters';
import React from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Text,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import ChartLoadingOverlay from '../common/ChartLoadingOverlay';

export const LeftYAxisLabel = ({
  viewBox,
  value,
  angle,
  style = {
    fontWeight: 600,
    fontSize: 10,
  },
  offset = 0,
}) => {
  const { x, y, height } = viewBox;
  return (
    <Text
      x={x + offset}
      y={(y * 1) / height + height / 2}
      textAnchor="middle"
      angle={angle}
      style={style}
    >
      {value}
    </Text>
  );
};

export const RightYAxisLabel = ({
  viewBox,
  value,
  angle,
  style = {
    fontWeight: 600,
    fontSize: 10,
  },
  offset = 0,
}) => {
  const { x, y, width, height } = viewBox;
  return (
    <Text
      x={x + width - offset}
      y={(y * 1) / height + height / 2}
      textAnchor="middle"
      angle={angle}
      style={style}
    >
      {value}
    </Text>
  );
};

function AppComposedChart({ data, children, loading = false }) {
  const isMobile = useMediaQuery(`(max-width: ${mediaQueryPoints.md}px)`);

  return (
    <div className="w-full h-full relative">
      {loading && <ChartLoadingOverlay loading={loading} />}
      <ResponsiveContainer
        width="100%"
        height="100%"
        className="border bg-white rounded-md shadow-sm"
      >
        <ComposedChart data={data}>
          {children ? (
            children
          ) : (
            <>
              <CartesianGrid stroke="#ccc" strokeDasharray="3 0" />
              <Tooltip
                formatter={(value, name, props) => {
                  return [
                    formatNumber(value),
                    name.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase()),
                  ];
                }}
              />
              <Legend
                layout="horizontal"
                verticalAlign="top"
                align="center"
                wrapperStyle={{ paddingBottom: 8, fontSize: 12 }}
                formatter={(value) => {
                  return value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
                }}
              />
              <XAxis
                dataKey="package_code"
                label={{
                  value: 'Package',
                  position: 'bottom',
                  offset: -10,
                  style: {
                    fontWeight: 600,
                    fontSize: 10,
                  },
                }}
                tick={{ fontSize: isMobile ? 9 : 10 }}
              />
              <YAxis
                yAxisId="left"
                label={<LeftYAxisLabel value="Revenue" angle={-90} offset={10} />}
                tick={{ fontSize: isMobile ? 9 : 10 }}
                tickFormatter={(value) => formatNumber(value)}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                label={<RightYAxisLabel value="Quantity" angle={90} offset={20} />}
                tick={{ fontSize: isMobile ? 9 : 10 }}
                tickFormatter={(value) => formatNumber(value)}
              />
              <Bar yAxisId="left" dataKey="total_revenue" fill="#6a9eeb" />
              <Line yAxisId="right" type="linear" dataKey="quantity" stroke="#ec6a89" />
            </>
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

export default AppComposedChart;
